import {
  Avatar,
  Button,
  CircularProgress,
  Container,
  Grid,
  Link,
  makeStyles,
  TextField,
  Typography
} from '@material-ui/core';
import React, { FC, useState } from 'react';
import { LockOutlined } from '@material-ui/icons';
import { useForm } from 'react-hook-form';
import Auth from '@aws-amplify/auth';

const useStyles = makeStyles(theme => ({
  paper: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1)
  },
  submit: {
    margin: theme.spacing(3, 0, 2)
  }
}));

export interface IFormSignUp {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

interface IProps {
  goSignIn: (data?: IFormSignUp) => void;
}

export const PasswordField = {
  required: 'This field is required',
  pattern: {
    // eslint-disable-next-line
    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\^$*.\[\]{}\(\)?\-\"!@#%&\/,><\':;|_~`])\S{8,99}$/,
    message:
      'Your password needs to: be at least 8 characters long, include both lower and upper case characters, include both number and special character'
  }
};

export const EmailField = {
  required: 'This field is required',
  pattern: {
    value: /[a-z0-9]+@[a-z]+\.[a-z]{2,3}/,
    message: 'Invalid email'
  }
};

export const SignUp: FC<IProps> = props => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [messageError, setMessageError] = useState(null);

  const {
    handleSubmit,
    register,
    formState: { errors }
  } = useForm<IFormSignUp>();
  const onSubmit = (data: IFormSignUp) => {
    setLoading(true);
    Auth.signUp({
      password: data.password,
      username: data.email,
      attributes: {
        email: data.email,
        name: data.firstName,
        middle_name: data.lastName
      }
    })
      .then(() => {
        setLoading(false);
        setMessageError(null);
        props.goSignIn(data);
      })
      .catch(error => {
        setMessageError(error?.message);
      })
      .finally(() => setLoading(false));
  };

  return (
    <Container component="main" maxWidth="xs">
      <div className={classes.paper}>
        <Avatar className={classes.avatar}>
          <LockOutlined />
        </Avatar>
        <Typography component="h1" variant="h5">
          Sign up
        </Typography>
        <form className={classes.form} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Email Address"
                autoFocus
                helperText={errors?.email?.message}
                error={!!errors?.email}
                name="email"
                inputRef={register(EmailField)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="normal"
                required
                type="password"
                fullWidth
                label="Password"
                error={!!errors?.password}
                helperText={
                  errors?.password?.message ||
                  'Choose a unique password with minimum 8 characters. Include at least 1 number, a mix of lowercase and uppercase letters and 1 symbol to make your password strong.'
                }
                name="password"
                inputRef={register(PasswordField)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="normal"
                required
                style={{ marginTop: 0 }}
                fullWidth
                label="First Name"
                helperText={errors?.firstName?.message}
                error={!!errors?.firstName}
                name="firstName"
                inputRef={register({
                  required: 'This field is required'
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Last Name"
                helperText={errors?.lastName?.message}
                error={!!errors?.lastName}
                name="lastName"
                inputRef={register({
                  required: 'This field is required'
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Phone"
                helperText={errors?.phoneNumber?.message}
                error={!!errors?.phoneNumber}
                name="phoneNumber"
                inputRef={register({
                  required: 'This field is required'
                })}
              />
            </Grid>
            <Grid item xs={12}></Grid>
          </Grid>
          {!messageError || (
            <Typography color="error">{messageError}</Typography>
          )}
          <Button
            type="button"
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
            onClick={handleSubmit(onSubmit)}
            disabled={loading}
          >
            {loading ? <CircularProgress size={16} /> : 'Sign Up'}
          </Button>
          <Grid container justify="flex-end">
            <Grid item>
              <Link
                style={{ cursor: 'pointer' }}
                onClick={() => props.goSignIn?.()}
                variant="body2"
              >
                Already have an account? Sign in
              </Link>
            </Grid>
          </Grid>
        </form>
      </div>
    </Container>
  );
};
